import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { ActionStatusGlyph, StatusBadge } from "@/accounts-payable/common";
import type {
  ApAgentAction,
  ApReviewQueueInvoice,
} from "@/accounts-payable/types";
import {
  actionOutcomeCopy,
  actionTitle,
  formatApDate,
  invoiceStatusMeta,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

export const ApInvoiceSegmentPreview = ({
  invoice,
  actions,
}: {
  invoice: ApReviewQueueInvoice;
  actions: ApAgentAction[];
}) => {
  const pending = actions.filter(isPendingReviewAction);
  // "Resolved" means decided — approved-but-still-applying actions belong here;
  // the glyph carries the in-flight state.
  const handled = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack align="start" gap={2} className="w-80">
      {/* Same identity line as the Review tab card: name, then status. */}
      <Stack
        direction="row"
        gap={2}
        align="center"
        className="w-full flex-wrap"
      >
        <Text variant="label" className="min-w-0 wrap-break-word">
          {invoice.vendor_name}
        </Text>
        <StatusBadge meta={invoiceStatusMeta(invoice)} />
      </Stack>
      {/* No total when there isn't one: a "-" read as a value of zero (the
          Review tab card's rule). */}
      <Text variant="caption" tone="muted">
        {invoice.invoice_number}
        {` · ${formatApDate(invoice.invoice_date)}`}
        {invoice.total_amount_cents
          ? ` · ${formatCentsOrDashIfZero(invoice.total_amount_cents)}`
          : null}
      </Text>

      {/* One neutral treatment for every action type, led by the status
          glyph — the review page's rule (a per-type hue read as status). */}
      {pending.length > 0 && (
        <Stack
          align="start"
          gap={1}
          className="w-full border-t border-line pt-2"
        >
          <Text variant="label" tone="muted">
            Needs review ({pending.length})
          </Text>
          {pending.map((action) => (
            <Stack
              key={action.id}
              direction="row"
              gap={2}
              align="start"
              className="min-w-0"
            >
              <ActionStatusGlyph
                action={action}
                outline
                className="size-4 flex-none"
              />
              <Text className="min-w-0 flex-1 wrap-break-word">
                {actionTitle(action)}
              </Text>
            </Stack>
          ))}
        </Stack>
      )}

      {handled.length > 0 && (
        <Stack
          align="start"
          gap={1}
          className="w-full border-t border-line pt-2"
        >
          <Text variant="label" tone="muted">
            Resolved ({handled.length})
          </Text>
          {handled.map((action) => (
            <Stack
              key={action.id}
              direction="row"
              gap={2}
              align="start"
              className="min-w-0"
            >
              <ActionStatusGlyph
                action={action}
                outline
                className="size-4 flex-none"
              />
              <Stack align="start" gap={0} className="min-w-0 flex-1">
                <Text tone="muted" className="wrap-break-word">
                  {actionTitle(action)}
                </Text>
                {/* The review page's outcome line: who decided, or a
                    deviation — red only for an apply failure. */}
                {actionOutcomeCopy(action) && (
                  <Text
                    variant="caption"
                    tone={
                      action.status === "apply_failed" ? "destructive" : "muted"
                    }
                  >
                    {actionOutcomeCopy(action)}
                  </Text>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
